import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { animate, stagger } from 'animejs';
import { supabase } from '../lib/supabase';
import { DeskLogo } from '../components/ui/DeskLogo';
import { DottedSurface } from '../components/ui/DottedSurface';

function mensagemDeErro(error) {
    const code = error.code;
    if (code === 'invalid_credentials') return 'Email ou senha incorretos';
    if (code === 'email_not_confirmed') return 'Email ainda não confirmado. Verifique sua caixa de entrada.';
    if (code === 'over_request_rate_limit' || error.status === 429)
        return 'Muitas tentativas. Aguarde um minuto e tente novamente.';
    if (code === 'user_banned') return 'Usuário bloqueado. Contate o administrador.';
    return error.message || 'Não foi possível entrar. Tente novamente em instantes.';
}

// ─── SVG filter: turbulência + displacement = liquid glass ───
function GlassFilter() {
    return (
        <svg style={{ display: 'none' }} aria-hidden="true">
            <filter id="glass-distortion" x="0%" y="0%" width="100%" height="100%" filterUnits="objectBoundingBox">
                <feTurbulence type="fractalNoise" baseFrequency="0.001 0.005" numOctaves="1" seed="17" result="turbulence" />
                <feComponentTransfer in="turbulence" result="mapped">
                    <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                    <feFuncG type="gamma" amplitude="0"  exponent="1"  offset="0"   />
                    <feFuncB type="gamma" amplitude="0"  exponent="1"  offset="0.5" />
                </feComponentTransfer>
                <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
                <feSpecularLighting in="softMap" surfaceScale="5" specularConstant="1" specularExponent="100" lightingColor="white" result="specLight">
                    <fePointLight x="-200" y="-200" z="300" />
                </feSpecularLighting>
                <feComposite in="specLight" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litImage" />
                <feDisplacementMap in="SourceGraphic" in2="softMap" scale="200" xChannelSelector="R" yChannelSelector="G" />
            </filter>
        </svg>
    );
}

// ─── GlassEffect: 3 camadas exatas do liquid-glass reference ───
function GlassEffect({ children, style, theme = 'dark' }) {
    const isDark = theme === 'dark';
    const fill = isDark
        ? 'rgba(4, 8, 11, 0.28)'          // escuro mas translúcido — pontos visíveis
        : 'rgba(255, 255, 255, 0.26)';     // branco translúcido no light

    return (
        <div style={{
            position: 'relative',
            display: 'flex',
            overflow: 'hidden',
            borderRadius: '28px',
            boxShadow: '0 6px 24px rgba(0,0,0,0.32), 0 0 40px rgba(0,0,0,0.14)',
            transitionTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 2.2)',
            transition: 'all 700ms',
            ...style,
        }}>
            {/* Layer 1 — backdrop blur + SVG displacement (distorção líquida) */}
            <div style={{
                position: 'absolute',
                inset: 0,
                zIndex: 0,
                overflow: 'hidden',
                borderRadius: '28px',
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
                filter: 'url(#glass-distortion)',
                isolation: 'isolate',
            }} />

            {/* Layer 2 — fill adaptativo ao tema */}
            <div style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                borderRadius: '28px',
                background: fill,
            }} />

            {/* Layer 3 — rim inset highlight */}
            <div style={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                borderRadius: '28px',
                overflow: 'hidden',
                boxShadow: isDark
                    ? 'inset 1px 1px 0 0 rgba(255,255,255,0.08), inset -1px -1px 0 1px rgba(255,255,255,0.04)'
                    : 'inset 2px 2px 1px 0 rgba(255,255,255,0.55), inset -1px -1px 1px 1px rgba(255,255,255,0.45)',
            }} />

            {/* Content */}
            <div style={{ position: 'relative', zIndex: 10, width: '100%' }}>
                {children}
            </div>
        </div>
    );
}

export default function Login() {
    const [email, setEmail]       = useState('');
    const [password, setPassword] = useState('');
    const [error, setError]       = useState(null);
    const [loading, setLoading]   = useState(false);
    const [showPass, setShowPass] = useState(false);
    const formRef = useRef(null);
    const theme   = localStorage.getItem('desk-theme') || 'dark';

    useEffect(() => {
        if (!formRef.current) return;
        animate(formRef.current.querySelectorAll('[data-stagger]'), {
            opacity: [0, 1],
            translateY: [16, 0],
            duration: 680,
            delay: stagger(65, { start: 80 }),
            ease: 'cubicBezier(0.16, 1, 0.3, 1)',
        });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
            setError(mensagemDeErro(error));
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'relative',
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            background: 'var(--canvas)',
        }}>
            <GlassFilter />

            {/* Fundo animado — ocupa tela inteira */}
            <DottedSurface
                theme={theme}
                style={{ position: 'fixed', inset: 0, zIndex: 0 }}
            />

            {/* Card liquid glass centralizado */}
            <GlassEffect theme={theme} style={{
                zIndex: 10,
                width: '100%',
                maxWidth: '420px',
                margin: '1.5rem',
            }}>
                <div ref={formRef} style={{ padding: '2.5rem 2.25rem 2rem' }}>
                    {/* Logo + badge */}
                    <div data-stagger style={{ textAlign: 'center', marginBottom: '1.75rem', opacity: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                            <div style={{ filter: 'drop-shadow(0 0 16px rgba(0,238,154,0.32))' }}>
                                <DeskLogo variant="symbol" size={52} theme={theme} />
                            </div>
                        </div>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            padding: '0.22rem 0.6rem',
                            borderRadius: 9999,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: 'var(--emerald)',
                            background: 'rgba(0,238,154,0.10)',
                            border: '1px solid rgba(0,238,154,0.28)',
                            fontFamily: 'var(--font-section, var(--font-sans))',
                            marginBottom: '0.75rem',
                        }}>
                            <Icon icon="ph:shield-check-bold" width={10} />
                            Acesso Seguro
                        </div>
                        <h1 style={{
                            fontSize: '1.4rem',
                            fontWeight: 700,
                            letterSpacing: '-0.025em',
                            color: 'var(--ink)',
                            lineHeight: 1.2,
                            fontFamily: 'var(--font-section, var(--font-sans))',
                            marginBottom: '0.3rem',
                        }}>
                            Bem-vindo de volta
                        </h1>
                        <p style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                            Entre com suas credenciais para acessar o Desk.
                        </p>
                    </div>

                    {/* Formulário */}
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                        {/* Email */}
                        <div data-stagger style={{ opacity: 0 }}>
                            <label style={{
                                display: 'block', fontSize: '0.7rem', fontWeight: 600,
                                color: 'var(--muted)', marginBottom: '0.4rem',
                            }} htmlFor="email">Email</label>
                            <div style={{ position: 'relative' }}>
                                <Icon icon="ph:envelope-simple-bold" width={14} style={{
                                    position: 'absolute', left: '0.875rem', top: '50%',
                                    transform: 'translateY(-50%)', color: 'var(--dim)', pointerEvents: 'none',
                                }} />
                                <input
                                    id="email" type="email" value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required placeholder="seu@email.com"
                                    autoComplete="email" className="input"
                                    style={{ paddingLeft: '2.4rem' }}
                                />
                            </div>
                        </div>

                        {/* Senha */}
                        <div data-stagger style={{ opacity: 0 }}>
                            <label style={{
                                display: 'block', fontSize: '0.7rem', fontWeight: 600,
                                color: 'var(--muted)', marginBottom: '0.4rem',
                            }} htmlFor="password">Senha</label>
                            <div style={{ position: 'relative' }}>
                                <Icon icon="ph:lock-simple-bold" width={14} style={{
                                    position: 'absolute', left: '0.875rem', top: '50%',
                                    transform: 'translateY(-50%)', color: 'var(--dim)', pointerEvents: 'none',
                                }} />
                                <input
                                    id="password" type={showPass ? 'text' : 'password'}
                                    value={password} onChange={e => setPassword(e.target.value)}
                                    required placeholder="••••••••"
                                    autoComplete="current-password" className="input"
                                    style={{ paddingLeft: '2.4rem', paddingRight: '2.8rem' }}
                                />
                                <button type="button" onClick={() => setShowPass(v => !v)} style={{
                                    position: 'absolute', right: '0.875rem', top: '50%',
                                    transform: 'translateY(-50%)', background: 'none', border: 'none',
                                    color: 'var(--dim)', cursor: 'pointer', padding: 0, display: 'flex',
                                }}>
                                    <Icon icon={showPass ? 'ph:eye-slash-bold' : 'ph:eye-bold'} width={14} />
                                </button>
                            </div>
                        </div>

                        {/* Erro */}
                        {error && (
                            <div data-stagger style={{
                                background: 'rgba(255,104,116,0.08)',
                                border: '1px solid rgba(255,104,116,0.22)',
                                borderRadius: '10px', padding: '0.55rem 0.8rem',
                                fontSize: '0.75rem', color: 'var(--danger-soft)',
                                display: 'flex', alignItems: 'center', gap: '0.45rem', opacity: 0,
                            }}>
                                <Icon icon="ph:warning-circle-bold" width={13} />
                                {error}
                            </div>
                        )}

                        {/* Submit */}
                        <div data-stagger style={{ opacity: 0, marginTop: '0.35rem' }}>
                            <button
                                type="submit" disabled={loading}
                                className="pill pill-primary pill-shimmer"
                                style={{ width: '100%', padding: '0.8rem', fontSize: '0.875rem', borderRadius: '12px' }}
                            >
                                {loading ? (
                                    <><Icon icon="svg-spinners:180-ring-with-bg" width={15} />Entrando...</>
                                ) : (
                                    <>Entrar<Icon icon="ph:arrow-right-bold" width={14} /></>
                                )}
                            </button>
                        </div>

                        {/* Esqueci */}
                        <div data-stagger style={{ textAlign: 'center', opacity: 0 }}>
                            <Link to="/redefinir-senha" style={{
                                fontSize: '0.7rem', color: 'var(--dim)', textDecoration: 'none',
                                display: 'inline-flex', alignItems: 'center', gap: '0.3rem', transition: 'color 0.18s',
                            }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--emerald)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}
                            >
                                <Icon icon="ph:question-bold" width={11} />
                                Esqueci minha senha
                            </Link>
                        </div>
                    </form>

                    {/* Rodapé */}
                    <div data-stagger style={{
                        marginTop: '2rem', paddingTop: '1rem',
                        borderTop: '1px solid rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: 0,
                    }}>
                        <span style={{ fontSize: '0.62rem', color: 'var(--hush)', letterSpacing: '0.04em' }}>
                            Acesso restrito · Orbion
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--emerald)', boxShadow: '0 0 6px var(--emerald)' }} />
                            <span style={{ fontSize: '0.6rem', color: 'var(--muted)', fontFamily: 'var(--font-mono)' }}>sistema online</span>
                        </div>
                    </div>
                </div>
            </GlassEffect>
        </div>
    );
}
